import { Pipe, PipeTransform } from "@angular/core";
import { MomentInput } from "moment";
import { formatDate } from "@helpers/date";
import { equal } from "@helpers/utils";
import { TranslationService } from "@core-services/translation.service";

interface DateInterface {
	date?: string[];
	time?: string[];

	dateSeperator?: string;
	timeSeperator?: string;
	dateTimeSeperator?: boolean;

	day?: string | false;
	month?: string | false;
	year?: string | false;

	hours?: string | false;
	minutes?: string | false;
	seconds?: string | false;

	raw?: string | false;
}

class DateFormat implements DateInterface {
	// Public date: string = "DD.MM.YYYY HH:mm:ss";

	public date = ["day", "month", "year"];
	public time = ["hours", "minutes", "seconds"];

	public dateSeperator: string = ".";
	public timeSeperator: string = ":";
	public dateTimeSeperator: boolean = false;

	public day: string | false = "DD";
	public month: string | false = "MM";
	public year: string | false = "YYYY";

	public hours: string | false = "HH";
	public minutes: string | false = "mm";
	public seconds: string | false = "ss";

	public raw: string | false = false;
}

@Pipe({
	name: "bp_date",
	standalone: false
})
export class BpDatePipe implements PipeTransform {
	private static default = new DateFormat();

	public transform(value: MomentInput, dateFormat: DateInterface = new DateFormat()): string {
		if (equal(value, new Date(0))) {
			return "-";
		}

		return formatDate(value, this.getFormat(dateFormat)).trim();
	}

	private assign(target: DateInterface, source: DateFormat): void {
		Object.keys(source).forEach((key) => {
			if (source[key] instanceof Array) {
				if (!target[key]) {
					target[key] = [];
					this.assign(target[key], source[key] as unknown as DateFormat);
				}
			}
			// Dont set overwritten values
			if (!target[key] && target[key] !== false) {
				target[key] = source[key];
			}
		});
	}

	public getFormat(dateFormat: DateInterface = new DateFormat()): string {
		this.assign(dateFormat, BpDatePipe.default);

		if (typeof dateFormat.raw === "string") {
			return dateFormat.raw;
		}

		return (
			["date", "time"]
				.map((k) =>
					dateFormat[k]
						// Filter all keys that were set tot false
						.filter((key) => dateFormat[key] !== false)
						.map((span) => {
							// Any key is NOT set to false
							if (dateFormat[span]) {
								return dateFormat[span];
							}
						})
						.map((s, index, array) => {
							// "seconds" or "years" mustnt have anything as suffix
							let seperator = "";
							// "year" doenst have seperator; time only has seperator inbetween
							if ((k === "date" && s !== dateFormat.year) || (k === "time" && index < array.length - 1)) {
								seperator = dateFormat[k + "Seperator"];
							}
							return s + seperator;
						})
						// Join to string
						.join("")
				)
				// Join with empty space
				.join(dateFormat.dateTimeSeperator ? ", " : " ")
		);
	}
}

@Pipe({
	name: "date_from_now",
	standalone: false
})
export class DateFromNow implements PipeTransform {
	public transform(value: string): string {
		const date = new Date(value);

		return this.timeAgo(date, TranslationService.getLanguageIsoCode());
	}

	private timeAgo(timestamp, locale = "en"): string {
		let value;
		const diff = (new Date().getTime() - timestamp.getTime()) / 1000;
		const minutes = Math.floor(diff / 60);
		const hours = Math.floor(minutes / 60);
		const days = Math.floor(hours / 24);
		const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

		if (days > 0) {
			return timestamp.toLocaleDateString("de-DE");
		} else if (hours > 0) {
			value = rtf.format(0 - hours, "hour");
		} else if (minutes > 0) {
			value = rtf.format(0 - minutes, "minute");
		} else {
			value = "notification_list_just_now";
		}
		return value;
	}
}

@Pipe({
	name: "date_locale",
	standalone: false
})
export class DateLocalePipe implements PipeTransform {
	public transform(dateInput: string, options?: Intl.DateTimeFormatOptions): string {
		const date = new Date(dateInput);
		const locale = TranslationService.getLanguageIsoCode();
		if (options) {
			return date.toLocaleDateString(locale, options);
		}
		return date.toLocaleDateString(locale, {
			day: "numeric",
			month: "numeric",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit"
		});
	}
}

// date_iso_format Transform the date into date iso format to send to API, example 2000-10-31T01:30:00.000-05:00
@Pipe({
	name: "date_iso_format",
	standalone: false
})
export class DateIsoFormat implements PipeTransform {
	public transform(dateInput: string): string {
		const date = new Date(dateInput);

		// Get the components of the date
		const year = date.getFullYear();
		const month = (date.getMonth() + 1).toString().padStart(2, "0"); // Month is 0-indexed
		const day = date.getDate().toString().padStart(2, "0");
		const hours = date.getHours().toString().padStart(2, "0");
		const minutes = date.getMinutes().toString().padStart(2, "0");
		const seconds = date.getSeconds().toString().padStart(2, "0");

		return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}Z`;
	}
}
